import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { IPendingRequest, ISaveRequestData } from './offline.interface';
import { catchError, concatMap, delay, filter, finalize, from, Observable, of, tap, toArray } from 'rxjs';
import { IOrderUpdate } from '../../typing/order.interface';
import {ISaveFiles} from  '../../../libs/ui-lib/offline/offline.interface';
import { OfflineService } from './offline.service';
import { OfflineFilesService } from './offliene.files.service';

@Injectable({ providedIn: 'root' })
export class OfflineStorageService {
    private readonly storeName = 'pending_requests';
    private http = inject(HttpClient);
    updateRequest = signal<boolean>(false);

    constructor(
        public offlineService: OfflineService,
        private offlineFilesService: OfflineFilesService
    ) {}

    private upDateRequest(requests: IPendingRequest[]): void {
        localStorage.setItem(this.storeName, JSON.stringify(requests));
    }

    private createPendingRequest(request: ISaveRequestData, id: number): IPendingRequest {
        return {
            date: request,
            id: id,
        };
    }

    private deserializeRequest(pending:  ISaveRequestData): HttpRequest<unknown> {
        return new HttpRequest(
            pending.method.toLowerCase(),
            pending.url,
            pending.body,
        );
    }

    private parserHttpRequest({body, url, method}: HttpRequest<unknown>): ISaveRequestData {
        return {
            body,
            url,
            method
        };
    }

    saveOfflineRequest(request: HttpRequest<unknown>): void {
        const body = request.body as IOrderUpdate;
        let orderId: number | null = body.id;

        if (request.body instanceof FormData) {
            orderId = Number(request.body.get('orderId'));
        }

        const pendingRequests = this.onGetRequest();
        const parserObj = this.parserHttpRequest(request);
        const existingIndex = pendingRequests.findIndex(item => item.date.url === request.url && item.id === orderId);

        if (existingIndex !== -1) {
            const existingBody = pendingRequests[existingIndex].date.body as IOrderUpdate;
            const cleanedBody = Object.fromEntries(
                Object.entries(body).filter(([_, value]) => value !== undefined)
            );
            parserObj.body = { ...existingBody, ...cleanedBody };
            pendingRequests[existingIndex] = this.createPendingRequest(parserObj, orderId);
        }else {
            pendingRequests.push(this.createPendingRequest(parserObj, orderId));
        }

        this.upDateRequest(pendingRequests);
    }

    onGetRequest(): IPendingRequest[] {
        return JSON.parse(localStorage.getItem(this.storeName) || '[]');
    }

    removeRequest(id?: number | null): void {
        if (!id) {return;}
        const requests = this.onGetRequest().filter(r => r.id !== id);
        this.upDateRequest(requests);
    }

    retryRequest(dataFiles: ISaveFiles[]): Observable<unknown> {
        const pendingRequests = this.onGetRequest();
        let isFile = false;
        if (!pendingRequests) {
            this.updateRequest.set(false);
            return of([]);
        }

        return from(pendingRequests).pipe(
            delay(1000),
            concatMap(
                res => {
                    if (res.date.url.includes('files') && res.id) {
                        isFile = true;
                        res.date.body = this.createFormDataWithFiles(res.id, dataFiles);
                    }
                    return this.http.request(this.deserializeRequest(res.date)).pipe(
                        catchError(() => {
                            return of(null); // Обрабатываем ошибки и возвращаем null
                        }),
                        filter(response => response !== null), // Фильтруем пустые результаты
                        tap(() => {
                            this.updateRequest.set(true);
                            this.removeRequest(res.id);
                            if (res.id && isFile) {
                                this.offlineFilesService.deleteFilesByOrderId(res.id);
                            }
                        })
                    );
                }
            ),
            toArray(), // Собираем результаты в массив после завершения всех запросов
            finalize(() => {
                this.updateRequest.set(false);
                // Обновляем состояние после завершения всех запросов
                this.offlineService.loadDateOrderOffline$.next();
                /// делаем тригер после того как отравили заявки кода работник был в офлайн и менажер назначал новые заявки.
            })
        );
    }

    private createFormDataWithFiles(orderId: number, dataFiles: ISaveFiles[]): FormData {
        const formData = new FormData();
        const files = dataFiles.find(item => item.orderId === orderId)?.files || [];

        formData.append('orderId', orderId.toString());

        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i], files[i].name);
        }

        return formData;
    }
}
